import { useCallback, useRef } from 'react';
import { notesAPI } from '@/lib/api';
import { decryptContent, deriveKey, encryptContent } from '@/lib/crypto';

/**
 * Encryption/decryption logic for notes.
 * Provides decryptNote (single) and decryptAllNotes (bulk disable).
 */
export function useEncryption({ encryptionKey, encryptionPassword, user, notes, setNotes }) {
  // Cache for per-note salt key derivations (persists across renders)
  const saltKeyCache = useRef({});

  const decryptNote = useCallback(async (note) => {
    if (note.is_encrypted && note.encryption_iv) {
      if (!encryptionKey) {
        return { ...note, _needsDecryption: true };
      }
      try {
        const content = await decryptContent(note.content, note.encryption_iv, encryptionKey);
        return { ...note, content, _decrypted: true };
      } catch {
        // Fast path failed — try per-note salt if it differs from profile salt
        const noteSalt = note.encryption_salt;
        if (noteSalt && encryptionPassword && noteSalt !== user?.encryption_salt) {
          try {
            if (!saltKeyCache.current[noteSalt]) {
              saltKeyCache.current[noteSalt] = await deriveKey(encryptionPassword, noteSalt);
            }
            const content = await decryptContent(note.content, note.encryption_iv, saltKeyCache.current[noteSalt]);
            return { ...note, content, _decrypted: true };
          } catch {
            return { ...note, _decryptFailed: true };
          }
        }
        return { ...note, _decryptFailed: true };
      }
    }
    return note;
  }, [encryptionKey, encryptionPassword, user?.encryption_salt]);

  // Bulk decrypt all encrypted notes and persist plaintext to backend.
  // Must be called BEFORE disabling encryption.
  const decryptAllNotes = useCallback(async (password) => {
    if (!password) throw new Error('Password required to decrypt notes');
    const encrypted = notes.filter((n) => n.is_encrypted && n.content);
    let decrypted = 0;
    let failed = 0;
    const keyCache = {};

    const getKey = async (salt) => {
      if (!salt) throw new Error('No salt available');
      if (!keyCache[salt]) keyCache[salt] = await deriveKey(password, salt);
      return keyCache[salt];
    };

    for (const note of encrypted) {
      try {
        let rawContent;
        if (note._decrypted) {
          rawContent = note.content;
        } else {
          const salt = note.encryption_salt || user?.encryption_salt;
          const key = await getKey(salt);
          try {
            rawContent = await decryptContent(note.content, note.encryption_iv, key);
          } catch {
            // In-memory content may be stale — fetch fresh from server
            const fresh = await notesAPI.get(note.id);
            const freshSalt = fresh.data.encryption_salt || salt;
            const freshKey = await getKey(freshSalt);
            rawContent = await decryptContent(fresh.data.content, fresh.data.encryption_iv, freshKey);
          }
        }
        await notesAPI.update(note.id, {
          content: rawContent,
          is_encrypted: false,
          encryption_iv: null,
          encryption_salt: null,
        });
        setNotes((prev) => prev.map((n) =>
          n.id === note.id
            ? { ...n, content: rawContent, is_encrypted: false, encryption_iv: null, encryption_salt: null, _decrypted: false, _needsDecryption: false }
            : n
        ));
        decrypted++;
      } catch (err) {
        console.error('[DecryptAll] Failed to decrypt note:', note.id, note.title, err);
        failed++;
      }
    }
    return { decrypted, failed, total: encrypted.length };
  }, [notes, user?.encryption_salt, setNotes]);

  // Retroactive encryption helper — encrypts unencrypted notes when encryption is enabled
  const encryptExistingNote = useCallback(async (note, key, salt) => {
    try {
      const encrypted = await encryptContent(note.content, key);
      await notesAPI.update(note.id, {
        content: encrypted.content,
        is_encrypted: true,
        encryption_iv: encrypted.iv,
        encryption_salt: salt,
      });
      return { ...note, is_encrypted: true, encryption_iv: encrypted.iv, encryption_salt: salt, _decrypted: true, _encryptingInProgress: false };
    } catch (err) {
      console.error('[EncryptExisting] Failed to encrypt note:', note.id, err);
      return { ...note, _encryptingInProgress: false };
    }
  }, []);

  return { decryptNote, decryptAllNotes, encryptExistingNote };
}
